import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { EigentuemerPrintOptionsMeta, GrundstueckPrintOptionsMeta, RechtPrintOptionsMeta } from '../gb-api';
import { HistoryMode } from './HistoryMode';

@Injectable()
export class AppAuszugCookieService {
  private readonly GRUNDSTUECK_COOKIE_MAPPING = new Map([
    ['GRUNDSTUECK_EREID', 'ereidsAufbereiten'],
    ['GRUNDSTUECK_SERVITUT_ID', 'standardrechtIdsAufbereiten'],
    ['GRUNDSTUECK_WEITERE_RECHTSGRUENDE', 'weitereRechtsgruendeAufbereiten'],
    ['GRUNDSTUECK_EIGENTUM', 'eigentumErweitern'],
    ['UID_BEI_NATUERLICHEN_PERSONEN', 'uidBeiNatuerlichenPersonenDrucken'],
    ['GRUNDSTUECK_PFANDRECHT_ID', 'pfandrechtIdsAufbereiten'],
    ['ANZEIGE_ANMERKUNGEN', 'anmerkungen'],
    ['ANZEIGE_VORMERKUNGEN', 'vormerkungen'],
    ['ANZEIGE_HAENGIGE_GESCHAEFTE', 'haengigeGeschaefteAufbereiten'],
    ['ANZEIGE_DIENSTBARKEITEN', 'dienstbarkeiten'],
    ['ANZEIGE_GRUNDLASTEN', 'grundlasten'],
    ['ANZEIGE_GRUNDSTUECK_BESCHREIBUNG', 'grundstueckBeschreibung'],
    ['ANZEIGE_GRUNDPFANDRECHTE', 'grundpfandrechte'],
    ['ANZEIGE_EIGENTUM', 'eigentum'],
    ['ANZEIGE_EIGENTUEMER_ADRESSE', 'eigentuemerMitAdresse'],
    ['ANZEIGE_VERTRAGSTEXTE', 'vertragstexteAufbereiten'],
    ['ANZEIGE_OEREBKATASTER', 'oerebKataster'],
    ['ANZEIGE_GEGENEINTRAEGE_DIT_DIENSTBARKEITEN', 'gegeneintraegeDITDienstbarkeitenDrucken']
  ]);

  private readonly EIGENTUEMER_COOKIE_MAPPING = new Map([
    ['EIGENTUEMER_ADRESSE', 'adresseAufbereiten'],
    ['UID_BEI_NATUERLICHEN_PERSONEN', 'uidBeiNatuerlichenPersonenDrucken']
  ]);

  private readonly RECHT_COOKIE_MAPPING = new Map([
    ['VERTRAGSTEXTE_RECHT', 'vertragstextAufbereiten']
  ]);

  private expiresDays = 365;
  private readonly falseString = 'false';
  private readonly trueString = 'true';

  constructor(private cookieService: CookieService) {
  }

  /**
   * As long we use JSF-Printing, we need to provide AnzeigeOptionen as Cookie Values.
   * The Default Anzeige Optionen are configured on the Server and read at the beginning {@Link AppPrintConfig}.
   */
  public async saveGrundstueckCookies(printConfig: GrundstueckPrintOptionsMeta, noOverwrite: boolean = false): Promise<void> {
    const cookieSettings = new Map([]);
    cookieSettings.set('VERTRAGSTEXTE', this.trueString);
    cookieSettings.set('GLAEUBIGER_ADRESSE', this.trueString);
    cookieSettings.set('ANZEIGE_AMTLICHER_WERT', this.trueString);
    cookieSettings.set('ANZEIGE_DOMINIERTE_GRUNDSTUECKE', this.trueString);
    cookieSettings.set('GRUNDSTUECK_GEGENEINTRAG_PFAND', this.trueString);
    cookieSettings.set('GRUNDSTUECK_GEGENEINTRAG_LR', this.trueString);
    await this.saveCookies(printConfig, this.GRUNDSTUECK_COOKIE_MAPPING, cookieSettings, noOverwrite);
  }

  public async saveEigentuemerCookies(printConfig: EigentuemerPrintOptionsMeta, noOverwrite?: boolean): Promise<void> {
    const cookieSettings = new Map([
      ['UEBRIGE_BERECHTIGTE_ADRESSE', this.trueString]
    ]);
    await this.saveCookies(printConfig, this.EIGENTUEMER_COOKIE_MAPPING, cookieSettings, noOverwrite ? noOverwrite : false);
  }

  public async saveRechtCookies(printConfig: RechtPrintOptionsMeta, noOverwrite?: boolean): Promise<void> {
    await this.saveCookies(printConfig, this.RECHT_COOKIE_MAPPING, new Map([]), noOverwrite ? noOverwrite : false);
  }

  public async updateHistorisiertCookie(historisierValue: boolean): Promise<void> {
    const cookieSettings = new Map([
      ['HISTORY', historisierValue.toString()],
      ['LOESCHGESCHAEFT', historisierValue.toString()]
    ]);
    for (let pair of cookieSettings.entries()) {
      const [key, value] = pair;
      await this.cookieStoreSave(key, value);
    }
  }

  public readHistorisiertCookie(): HistoryMode.HistoryMode {
    if (this.cookieService.get('HISTORY') === 'true') {
      return HistoryMode.HistoryMode.MIT_HIST;
    } else {
      return HistoryMode.HistoryMode.OHNE_HIST;
    }
  }

  private async saveCookies(printConfig: any, cookieMapping: Map<string, string>, cookieSettings: Map<{}, {}>, noOverwrite: boolean): Promise<void> {
    cookieMapping.forEach((printOptionName: string, cookieName: string) => {
      cookieSettings.set(cookieName, printConfig.hasOwnProperty(printOptionName) ? printConfig[printOptionName] : this.falseString);
    });
    for (let pair of cookieSettings.entries()) {
      const [key, value] = pair;
      if (!noOverwrite || !this.cookieService.get(key as string)) {
        await this.cookieStoreSave(key as string, value as string);
      }
    }
  }

  public async cookieStoreSave(key: string, value: string): Promise<void> {
    // TODO: Remove when cookieStore is correctly typed in TypeScript
    // @ts-ignore
    const cookieStore = window.cookieStore;
    const expiresInMilliseconds: number = Date.now() + this.expiresDays * 24 * 60 * 60 * 1000;
    if (location.protocol === 'https:') {
      const cookie = {
        name: key,
        value: value,
        expires: expiresInMilliseconds,
        path: '/',
        secure: true
      };
      // @ts-ignore
      await cookieStore.set(cookie);
    } else {
      const cookie = {
        name: key,
        value: value,
        expires: expiresInMilliseconds,
        path: '/'
      };
      // @ts-ignore
      await cookieStore.set(cookie);
    }
  }

  public readGrundstueckCookies(model: GrundstueckPrintOptionsMeta): GrundstueckPrintOptionsMeta {
    return this.readCookies(model, this.GRUNDSTUECK_COOKIE_MAPPING);
  }

  public readEigentuemerCookies(model: GrundstueckPrintOptionsMeta): GrundstueckPrintOptionsMeta {
    return this.readCookies(model, this.EIGENTUEMER_COOKIE_MAPPING);
  }

  public readRechtCookies(model: GrundstueckPrintOptionsMeta): GrundstueckPrintOptionsMeta {
    return this.readCookies(model, this.RECHT_COOKIE_MAPPING);
  }

  private readCookies(model: any, cookieMapping: Map<string, string>): any {
    cookieMapping.forEach((printOptionName: string, cookieName: string) => {
      let value: boolean = false;
      if (this.cookieService.get(cookieName) === this.trueString) {
        value = true;
      }
      model[printOptionName] = value;
    });
    return model;
  }
}
